import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormFactoryComponent } from '../../../../shared/components/form-factory/form-factory.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import {
	BusinessEntityDetailsCreateRequest,
	BusinessEntityDetailsUpdateRequest,
	CreateBusinessEntityRequest,
	ManagementBusinessEntityControllerService,
	ManagementBusinessEntityResponse,
	UpdateBusinessEntityRequest,
} from 'api-contract-notices';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { take } from 'rxjs/internal/operators/take';
import { SharedService } from '../../../../shared/services/shared.service';
import { MatButtonModule } from '@angular/material/button';
import { Constants } from '../../../../shared/interfaces/constants';
import { UtilsService } from '../../../../shared/services/utils.service';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { startWith } from 'rxjs/internal/operators/startWith';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';
import { distinctUntilChanged } from 'rxjs/internal/operators/distinctUntilChanged';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subject } from 'rxjs/internal/Subject';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { environment } from '../../../../../environments/environment';

interface InputData {
	mode: 'NEW' | 'EDIT' | 'DELETE' | 'DELETE_REASSIGN' | 'PENDING';
	type: string;
	item: ManagementBusinessEntityResponse;
}

@Component({
	selector: 'app-business-entity',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		TranslateModule,
		MatIconModule,
		MatButtonModule,
		MatProgressSpinnerModule,
		MatDividerModule,
		MatDialogModule,
		FormFactoryComponent,
		MatAutocompleteModule,
		MatCheckboxModule,
	],
	templateUrl: './business-entity.html',
	styleUrl: './business-entity.scss',
})
export class BusinessEntityComponent implements OnInit, OnDestroy {
	private onDestroy: Subject<boolean> = new Subject();
	externalAssets = environment.reseller.externalAssets;
	formGroup: FormGroup = new FormGroup({});

	availableLanguages = Constants.AVAILABLE_LANGUAGES;
	avaliableLanguagesCodes = Constants.AVAILABLE_LANGUAGES.map(item => item.code);

	itemRelatedData = 0;
	filteredBusinessEntities: ManagementBusinessEntityResponse[] | undefined;

	apiLoading = {
		management: false,
	};

	constructor(
		public dialogRef: MatDialogRef<BusinessEntityComponent>,
		@Inject(MAT_DIALOG_DATA) public data: InputData,
		private fb: FormBuilder,
		public utils: UtilsService,
		private apiContractNoticeBusinessEntity: ManagementBusinessEntityControllerService,
		private sharedService: SharedService,
		private translate: TranslateService
	) {
		switch (this.data.mode) {
			case 'NEW':
			case 'EDIT': {
				this.formGroup = this.fb.group({
					address: [],
					city: [],
					country: [],
					embs: [, [Validators.minLength(6), Validators.maxLength(8)]],
				});

				this.avaliableLanguagesCodes.forEach((code, index) => {
					let componentName = 'id' + this.utils.titleCase(code);
					let componentValue = this.data.item ? this.data.item.details?.find((detail: any) => detail.lang === code.toUpperCase())?.id : null;
					let control: FormControl = new FormControl();
					control = new FormControl(componentValue ? componentValue : '');
					this.formGroup.addControl(componentName, control);

					componentName = 'name' + this.utils.titleCase(code);
					componentValue = this.data.item ? this.data.item.details?.find((detail: any) => detail.lang === code.toUpperCase())?.name : null;
					control = new FormControl(componentValue ? componentValue : '', index === 0 ? Validators.required : null);
					this.formGroup.addControl(componentName, control);
				});

				if (this.data.item) {
					this.formGroup.patchValue(this.data.item);
				}
				break;
			}
			case 'DELETE': {
				this.getRelatedData();

				this.formGroup = this.fb.group({
					reassignTo: [''],
					reassign: [false, [Validators.required]],
				});
				break;
			}
		}
	}

	ngOnInit(): void {
		if (this.formGroup.controls['reassignTo']) {
			this.formGroup.controls['reassignTo'].valueChanges.pipe(startWith(''), debounceTime(300), distinctUntilChanged(), takeUntil(this.onDestroy)).subscribe(searchTerm => {
				this.searchBusinessEntity(searchTerm);
			});
		}
	}

	ngOnDestroy(): void {
		this.onDestroy.next(true);
	}

	displayFn(businessEntity: ManagementBusinessEntityResponse): string {
		return businessEntity && businessEntity.id ? this.utils.getParameterByLanguageScript(businessEntity, 'name', true) : '';
	}

	checkEmbsValue(event: any) {
		var numbersOnly = event.target.value.replace(/\D/g, '');
		this.formGroup.controls['embs'].setValue(numbersOnly);
	}

	action(customAction?: string) {
		const action = customAction ? customAction : this.data.mode;
		switch (this.data.type) {
			case 'BUSINESS_ENTITY': {
				if (action === 'NEW') {
					this.createBusinessEntity();
				} else if (action === 'EDIT') {
					this.updateBusinessEntity();
				} else if (action === 'DELETE' || action === 'DELETE_REASSIGN') {
					this.deleteBusinessEntity();
				} else if (action === 'VALIDATION') {
					if (this.formGroup && this.formGroup.valid && Object.keys(this.formGroup.controls).length) {
						let valid = false;
						Object.keys(this.formGroup.controls).some(controlName => {
							return (valid = this.formGroup.controls[controlName].value);
						});
						return true;
					}
				}
				break;
			}
		}
		return false;
	}

	closeDialog(data?: ManagementBusinessEntityResponse) {
		this.dialogRef.close(data);
	}

	//** BUSINESS_ENTITY methods */
	private createBusinessEntity() {
		this.apiLoading.management = true;
		const businessEntityDto: CreateBusinessEntityRequest = {
			embs: this.formGroup.controls['embs'].value,
			address: this.formGroup.controls['address'].value,
			city: this.formGroup.controls['city'].value,
			country: this.formGroup.controls['country'].value,
			details: this.avaliableLanguagesCodes
				.map(langCode => ({
					name: this.formGroup.controls['name' + this.utils.titleCase(langCode)].value,
					lang: (<any>BusinessEntityDetailsCreateRequest.LangEnum)[langCode.toUpperCase()],
				}))
				.filter(details => details.name),
		};

		this.apiContractNoticeBusinessEntity
			.createBusinessEntity(this.utils.validateRequestObject(businessEntityDto))
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
						type: 'SUCCESS',
					});
					this.closeDialog(response);
				},
				error: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
					this.apiLoading.management = false;
				},
			});
	}

	private updateBusinessEntity() {
		this.apiLoading.management = true;
		const businessEntityDto: UpdateBusinessEntityRequest = {
			embs: this.formGroup.controls['embs'].value || null,
			address: this.formGroup.controls['address'].value,
			city: this.formGroup.controls['city'].value,
			country: this.formGroup.controls['country'].value,
			details: this.avaliableLanguagesCodes.map(langCode => ({
				id: this.formGroup.controls['id' + this.utils.titleCase(langCode)].value,
				name: this.formGroup.controls['name' + this.utils.titleCase(langCode)].value,
				lang: (<any>BusinessEntityDetailsUpdateRequest.LangEnum)[langCode.toUpperCase()],
			})),
		};

		this.apiContractNoticeBusinessEntity
			.updateBusinessEntity(this.utils.validateRequestObject(businessEntityDto), this.data.item.id)
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
						type: 'SUCCESS',
					});
					this.closeDialog(response);
				},
				error: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
					this.apiLoading.management = false;
				},
			});
	}

	private deleteBusinessEntity() {
		this.apiLoading.management = true;
		if (this.data.item && this.data.item.id) {
			const reassignTo: ManagementBusinessEntityResponse = this.formGroup.controls['reassignTo'].value;

			this.apiContractNoticeBusinessEntity
				.deleteBusinessEntity(this.data.item.id, reassignTo ? reassignTo.id : undefined)
				.pipe(take(1))
				.subscribe({
					next: () => {
						this.sharedService.showNotification.next({
							message: this.translate.instant('NOTIFICATIONS.RECORD_DELETED'),
							type: 'SUCCESS',
						});
						this.apiLoading.management = false;
						this.closeDialog();
					},
					error: (err: any) => {
						this.sharedService.showNotification.next({
							message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
							type: 'ERROR',
						});
						this.apiLoading.management = false;
					},
				});
		}
	}

	private getRelatedData() {
		this.apiLoading.management = true;
		this.apiContractNoticeBusinessEntity
			.getBusinessEntityRelatedToSize(this.data.item.id)
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.itemRelatedData = response.relatedItemsSize || 0;
					this.data.mode = this.itemRelatedData ? 'DELETE_REASSIGN' : 'DELETE';
					this.apiLoading.management = false;
				},
			});
	}

	private searchBusinessEntity(searchTerm: string) {
		if (searchTerm && typeof searchTerm === 'string') {
			this.apiContractNoticeBusinessEntity
				.getBusinessEntitiesPageable({ businessEntity: searchTerm }, 0, 100)
				.pipe(take(1))
				.subscribe({
					next: response => {
						this.filteredBusinessEntities = response.content;
					},
				});
		}
	}
	//** (END)BUSINESS_ENTITY methods */
}
